import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';

// Create styles for Standard Property Proposal PDF
const styles = StyleSheet.create({
  // Cover Page
  coverPage: {
    position: 'relative',
    backgroundColor: '#1E3A5F',
  },
  coverBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.4,
  },
  coverContent: {
    flex: 1,
    padding: 30,
    paddingTop: 25,
    position: 'relative',
    zIndex: 1,
  },
  coverPageInnerView: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  coverLogo: {
    width: 200,
    marginBottom: 180,
  },
  coverTitleContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  coverTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    marginBottom: 25,
    letterSpacing: 2,
    lineHeight: 1.3,
  },
  coverPartnership: {
    fontSize: 18,
    color: 'white',
    textAlign: 'center',
    marginBottom: 15,
    fontStyle: 'italic',
  },
  coverClientName: {
    fontSize: 26,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    marginBottom: 40,
    fontStyle: 'italic',
  },
  coverDescription: {
    fontSize: 10,
    color: 'white',
    textAlign: 'center',
    lineHeight: 1.6,
    paddingHorizontal: 30,
    position: 'absolute',
    bottom: 90,
    left: 30,
    right: 30,
  },
  coverFooter: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  coverPhone: {
    fontSize: 18,
    color: 'white',
    textAlign: 'center',
    marginBottom: 8,
  },
  coverWebsite: {
    fontSize: 14,
    color: 'white',
    textAlign: 'center',
  },

  // Content Page
  contentPage: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.4,
    paddingTop: 110, // Space for fixed header (header height ~106px)
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#1E3A5F',
    paddingHorizontal: 25,
    paddingVertical: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLogo: {
    width: 150,
  },
  headerPattern: {
    width: 120,
    height: 70,
    opacity: 0.5,
  },
  content: {
    padding: 35,
    paddingTop: 10,
    flex: 1,
  },

  // Typography
  pageTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#5D3A8E',
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#5D3A8E',
    marginBottom: 10,
    marginTop: 15,
  },
  subsectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
    marginTop: 10,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: 10,
    textAlign: 'justify',
  },
  boldText: {
    fontWeight: 'bold',
  },

  // Letter styles
  dateText: {
    fontSize: 10,
    textAlign: 'right',
    marginBottom: 20,
  },
  recipientText: {
    fontSize: 10,
    marginBottom: 3,
  },
  salutation: {
    fontSize: 10,
    marginTop: 20,
    marginBottom: 15,
  },

  // Key Information Table
  keyInfoTable: {
    border: '1 solid #1E3A5F',
    marginTop: 15,
    marginBottom: 15,
  },
  keyInfoHeader: {
    backgroundColor: '#1E3A5F',
    flexDirection: 'row',
  },
  keyInfoHeaderCell: {
    width: '50%',
    padding: 8,
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
  keyInfoRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #ddd',
  },
  keyInfoRowLast: {
    flexDirection: 'row',
  },
  keyInfoLabel: {
    width: '50%',
    padding: 8,
    fontSize: 9,
    backgroundColor: '#f5f5f5',
    borderRight: '1 solid #ddd',
  },
  keyInfoValue: {
    width: '50%',
    padding: 8,
    fontSize: 9,
  },

  // Table of Contents
  tocItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingRight: 20,
  },
  tocText: {
    fontSize: 10,
  },
  tocPage: {
    fontSize: 10,
  },

  // Bullet Points
  bulletList: {
    marginLeft: 15,
    marginBottom: 10,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  bullet: {
    width: 15,
    fontSize: 10,
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    lineHeight: 1.4,
  },

  // Liability Tables
  liabilityTable: {
    border: '1 solid #1E3A5F',
    marginTop: 15,
    marginBottom: 15,
  },
  liabilityHeader: {
    backgroundColor: '#1E3A5F',
    flexDirection: 'row',
  },
  liabilityHeaderCell: {
    padding: 8,
    color: 'white',
    fontSize: 9,
    fontWeight: 'bold',
    borderRight: '1 solid #16304D',
  },
  liabilityHeaderCellLast: {
    padding: 8,
    color: 'white',
    fontSize: 9,
    fontWeight: 'bold',
  },
  liabilityRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #ddd',
    flexWrap: 'wrap',
  },
  liabilityRowLast: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  liabilityCell: {
    padding: 6,
    fontSize: 8,
    borderRight: '1 solid #ddd',
    flexShrink: 1,
    wordBreak: 'break-word',
    overflow: 'hidden',
  },
  liabilityCellLast: {
    padding: 6,
    fontSize: 8,
    flexShrink: 1,
    wordBreak: 'break-word',
    overflow: 'hidden',
  },
  liabilityLabelCell: {
    padding: 6,
    fontSize: 8,
    fontWeight: 'bold',
    borderRight: '1 solid #ddd',
    backgroundColor: '#f9f9f9',
    flexShrink: 1,
  },

  // Signature Section
  signatureBox: {
    border: '1 solid #ddd',
    padding: 10,
    marginVertical: 15,
    width: 250,
  },
  signatureImage: {
    width: 200,
    height: 60,
    marginBottom: 5,
  },
  signatureName: {
    fontSize: 10,
    marginTop: 10,
    marginBottom: 3,
  },
  signatureTitle: {
    fontSize: 9,
    color: '#666',
    marginBottom: 3,
  },
  signatureLine: {
    borderBottom: '1 dotted #333',
    marginVertical: 8,
    width: 300,
  },

  // Final Page
  finalPage: {
    backgroundColor: '#1E3A5F',
    flex: 1,
  },
  finalContent: {
    flex: 1,
    padding: 40,
  },
  finalLogo: {
    width: 200,
    marginBottom: 200,
  },
  finalAddress: {
    position: 'absolute',
    bottom: 100,
    left: 40,
  },
  finalAddressText: {
    fontSize: 11,
    color: 'white',
    marginBottom: 3,
  },
  finalContactText: {
    fontSize: 11,
    color: 'white',
    marginTop: 10,
    marginBottom: 3,
  },

  // Highlighted text
  highlightedText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#5D3A8E',
    marginTop: 15,
    marginBottom: 10,
  },

  // Note text
  noteText: {
    fontSize: 10,
    fontWeight: 'bold',
    marginTop: 15,
  },
});

// Format date helper with ordinal suffix (1st, 2nd, 3rd, etc.)
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  const day = date.getDate();
  const ordinalSuffix = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
  const month = date.toLocaleDateString('en-GB', { month: 'long' });
  const year = date.getFullYear();

  return `${weekday}, ${day}${ordinalSuffix(day)} ${month} ${year}`;
};

// Table of Contents items
const tocItems = [
  { title: 'Our Guardian Security Solution', page: 6 },
  { title: 'Authorisation for the Use of the Property', page: 7 },
  { title: 'Duration', page: 7 },
  { title: 'Bespoke Property Management Software', page: 8 },
  { title: 'General Data Protection Regulation (GDPR)', page: 8 },
  { title: 'Liability Insurance', page: 9 },
  { title: 'Marketing the Property and Neighbours', page: 9 },
  { title: 'Liability Insurance', page: 9 },
  { title: 'Building Insurance', page: 9 },
  { title: 'Risk Management', page: 9 },
  { title: 'Changes to Property Agreement', page: 9 },
  { title: 'Force Majeure', page: 10 },
  { title: 'Marketing the Property', page: 11 },
  { title: 'Neighbours', page: 11 },
  { title: 'General Liability Schedule', page: 12 },
  { title: 'Terms and Conditions', page: 12 },
  { title: 'Utility Liability Schedule', page: 12 },
  { title: 'Health & Safety Liability Schedule', page: 13 },
  { title: 'Maintenance Liability Schedule', page: 14 },
  { title: 'Definitions', page: 15 },
  { title: 'Interpretations', page: 16 },
  { title: 'Accepting this Offer', page: 17 },
  { title: 'Terms and Conditions', page: 18 },
  { title: "GGM's Obligation", page: 19 },
  { title: 'Vacating the Property after Termination', page: 20 },
  { title: 'Declarations, Warranties and Miscellaneous', page: 21 },
  { title: 'Notices', page: 22 },
];

// General Liability items - function to generate dynamic data
const getGeneralLiabilityItems = (data) => [
  { item: 'My-Globe Subscription', works: data.npp_myglobe_subscription_rm || '', payment: data.npp_myglobe_subscription_rp || '', cost: data.npp_myglobe_subscription_cow || '' },
  { item: 'Management fee', works: data.npp_management_fee_rm || '', payment: data.npp_management_fee_rp || '', cost: data.npp_management_fee_cow || '' },
  { item: 'Building Insurance', works: data.npp_building_insurance_rm || '', payment: data.npp_building_insurancee_rp || '', cost: data.npp_building_insurance_cow || '' },
  { item: 'Public Liability Insurance', works: data.npp_public_liability_insurance_rm || '', payment: data.npp_public_liability_insurance_rp || '', cost: data.npp_public_liability_insurance_cow || '' },
  { item: '24/7 SIA Security', works: data.npp_sia_security_rm || '', payment: data.npp_sia_security_rp || '', cost: data.npp_sia_security_cow || '' },
  { item: '24/7 Caretaker', works: data.npp_catetaker_rm || '', payment: data.npp_catetaker_rp || '', cost: data.npp_catetaker_cow || '' },
  { item: 'Access and Egress', works: data.npp_access_egress_rm || '', payment: data.npp_access_egress_rp || '', cost: data.npp_access_egress_cow || '' },
  { item: 'Monthly Inspections', works: data.npp_monthly_inspections_rm || '', payment: data.npp_monthly_inspections_rp || '', cost: data.npp_monthly_inspections_cow || '' },
  { item: 'Weekly Inspections', works: data.npp_weekly_inspections_rm || '', payment: data.npp_weekly_inspections_rp || '', cost: data.npp_weekly_inspections_cow || '' },
  { item: 'HMO Licence', works: data.npp_hmo_liscense_rm || '', payment: data.npp_hmo_liscense_rp || '', cost: data.npp_hmo_liscense_cow || '' },
  { item: 'Selective Licence', works: data.npp_selective_license_rm || '', payment: data.npp_selective_license_rp || '', cost: data.npp_selective_license_cow || '' },
  { item: 'Floor plans', works: data.npp_floor_plans_rm || '', payment: data.npp_floor_plans_rp || '', cost: data.npp_floor_plans_cow || '' },
  { item: 'VOA Application', works: data.npp_voa_application_rm || '', payment: data.npp_voa_application_rp || '', cost: data.npp_voa_application_cow || '' },
];

// Utility Liability items - function to generate dynamic data
const getUtilityLiabilityItems = (data) => [
  { item: 'Gas', works: data.npp_gas_rm || '', payment: data.npp_gas_rp || '', supplier: data.npp_gas_csan || '' },
  { item: 'Electricity', works: data.npp_electricity_rm || '', payment: data.npp_electricity_rp || '', supplier: data.npp_electricity_csan || '' },
  { item: 'Water', works: data.npp_water_rm || '', payment: data.npp_water_rp || '', supplier: data.npp_water_csan || '' },
  { item: 'Business Rates', works: data.npp_business_rates_rm || '', payment: data.npp_business_rates_rp || '', supplier: data.npp_business_rates_csan || '' },
  { item: 'Council Tax', works: data.npp_council_tax_rm || '', payment: data.npp_council_tax_rp || '', supplier: data.npp_council_tax_csan || '' },
  { item: 'Bins', works: data.npp_bins_rm || '', payment: data.npp_bins_rp || '', supplier: data.npp_bins_csan || '' },
];

// Health & Safety items - function to generate dynamic data
const getHealthSafetyItems = (data) => [
  { item: 'Gas Safe certification & Renewal', works: data.npp_gas_safe_rm || '', payment: data.npp_gas_safe_rp || '', required: data.npp_gas_safe_dr || '', cost: data.npp_gas_safe_cow || '' },
  { item: 'NIC Certification & Renewal', works: data.npp_nic_rm || '', payment: data.npp_nic_rp || '', required: data.npp_nic_dr || '', cost: data.npp_nic_cow || '' },
  { item: 'Legionella Testing Certification', works: data.npp_legionella_rm || '', payment: data.npp_legionella_rp || '', required: data.npp_legionella_dr || '', cost: data.npp_legionella_cow || '' },
  { item: 'Asbestos Register', works: data.npp_asbestos_rm || '', payment: data.npp_asbestos_rp || '', required: data.npp_asbestos_dr || '', cost: data.npp_asbestos_cow || '' },
  { item: 'Fire Risk Assessment (FRA)', works: data.npp_fire_risk_rm || '', payment: data.npp_fire_risk_rp || '', required: data.npp_fire_risk_dr || '', cost: data.npp_fire_risk_cow || '' },
  { item: 'Fire Alarm Certification', works: data.npp_fire_alarm_rm || '', payment: data.npp_fire_alarm_rp || '', required: data.npp_fire_alarm_dr || '', cost: data.npp_fire_alarm_cow || '' },
  { item: 'Emergency lighting Certification', works: data.npp_emergency_lighting_rm || '', payment: data.npp_emergency_lighting_rp || '', required: data.npp_emergency_lighting_dr || '', cost: data.npp_emergency_lighting_cow || '' },
  { item: 'Fire Safety Provisions', works: data.npp_fire_safety_rm || '', payment: data.npp_fire_safety_rp || '', required: data.npp_fire_safety_dr || '', cost: data.npp_fire_safety_cow || '' },
  { item: 'Fire Extinguisher Servicing', works: data.npp_fire_extinguisher_rm || '', payment: data.npp_fire_extinguisher_rp || '', required: data.npp_fire_extinguisher_dr || '', cost: data.npp_fire_extinguisher_cow || '' },
];

// Maintenance items - function to generate dynamic data
const getMaintenanceItems = (data) => [
  { item: 'Initial fit out and clearance works', works: data.npp_initial_fitout_rm || '', payment: data.npp_initial_fitout_rp || '', cost: data.npp_initial_fitout_cow || '' },
  { item: 'Locks and Keys', works: data.npp_locks_keys_rm || '', payment: data.npp_locks_keys_rp || '', cost: data.npp_locks_keys_cow || '' },
  { item: 'Ongoing internal maintenance repairs', works: data.npp_ongoing_internal_maintenance_rm || '', payment: data.npp_ongoing_internal_maintenance_rp || '', cost: data.npp_ongoing_internal_maintenance_cow || '' },
  { item: 'Boiler repair and/or replacement', works: data.npp_boiler_repair_rm || '', payment: data.npp_boiler_repair_rp || '', cost: data.npp_boiler_repair_cow || '' },
  { item: 'Ongoing external maintenance repairs; structure and fabric of the Property, all windows, the roof, guttering and extremities', works: data.npp_ongoing_external_maintenance_rm || '', payment: data.npp_ongoing_external_maintenance_rp || '', cost: data.npp_ongoing_external_maintenance_cow || '' },
  { item: 'Cleaning Internal Areas', works: data.npp_cleaning_areas_rm || '', payment: data.npp_cleaning_areas_rp || '', cost: data.npp_cleaning_areas_cow || '' },
  { item: 'Emergency Repairs', works: data.npp_emergency_repairs_rm || '', payment: data.npp_emergency_repairs_rp || '', cost: data.npp_emergency_repairs_cow || '' },
  { item: 'Gardening', works: data.npp_gardening_rm || '', payment: data.npp_gardening_rp || '', cost: data.npp_gardening_cow || '' },
];

// Header Component - fixed prop ensures it appears on all pages including overflow pages
const PageHeader = () => (
  <View style={styles.header} fixed>
    <Image src="/assets/images/GG_Logo_white.png" style={styles.headerLogo} />
    <Image src="/assets/images/reports/property_proposal_header_new.png" style={styles.headerPattern} />
  </View>
);

// Helper to clean address - remove double commas and extra spaces
const cleanAddress = (address) => {
  if (!address) return '';
  return address
    .replace(/,\s*,/g, ',')  // Remove double commas
    .replace(/\s+/g, ' ')     // Remove extra spaces
    .trim();
};

const StandardProposalPDF = ({ data }) => {
  if (!data) return null;

  const clientName = data.client || data.client_name || '';
  // Get contact name, treating 'N/A' as empty
  const rawContactName = data.client_contact_name || data.contact_name || '';
  const contactName = (rawContactName && rawContactName !== 'N/A') ? rawContactName : '';
  const contactTitle = data.client_contact_title || '';
  const contactAddress = cleanAddress(data.client_address || '');
  const propertyAddress = cleanAddress(`${data.property_address_1 || ''} ${data.property_address_2 || ''} ${data.property_address_3 || ''} ${data.property_town || ''}, ${data.property_postcode || ''}`);
  const proposalDate = formatDate(data.created_at || new Date());
  // For salutation, use contact name or fallback to "Sir/Madam"
  const salutationName = contactName || 'Sir/Madam';

  return (
    <Document>
      {/* Page 1: Cover Page */}
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverPageInnerView}>

          {/* Background image - uses building image as fallback */}
          <Image
            src="/assets/images/reports/building_first_page.jpg"
            style={styles.coverBackground}
          />
          <View style={styles.coverContent}>
            <Image
              src="/assets/images/GG_Logo_white.png"
              style={styles.coverLogo}
            />
            <View style={styles.coverTitleContainer}>
              <Text style={styles.coverTitle}>PROPERTY PROTECTION{'\n'}PROPOSAL</Text>
              <Text style={styles.coverPartnership}>In partnership with</Text>
              <Text style={styles.coverClientName}>{clientName}</Text>
            </View>
          </View>
          <Text style={styles.coverDescription}>
            Global Guardians Management is a proud member of the British Security Industry Association (BSIA) and has been integral in the development of the BS8584:2015 standard for vacant property protection.
          </Text>
          <View style={styles.coverFooter}>
            <Text style={styles.coverPhone}>0800 368 9601</Text>
            <Text style={styles.coverWebsite}>www.global-guardians.co.uk</Text>
          </View>
        </View>
      </Page>

      {/* Page 2: Letter - Recipient */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.recipientText}>To: {contactName}</Text>
          {contactTitle && <Text style={styles.recipientText}>{contactTitle}</Text>}
          <Text style={styles.recipientText}>{clientName}</Text>
          {contactAddress && <Text style={styles.recipientText}>{contactAddress}</Text>}
        </View>
      </Page>

      {/* Page 3: Empty page */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={{ color: 'white' }}> </Text>
        </View>
      </Page>

      {/* Page 4: Letter Content */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.dateText}>{proposalDate}</Text>
          <Text style={styles.salutation}>Dear {salutationName},</Text>

          <Text style={styles.paragraph}>
            Thank you for your invitation to provide <Text style={styles.boldText}>{clientName}</Text> ("The Owner") with a Proposal to supply Global Guardians Management Limited (GGM) services at <Text style={styles.boldText}>{propertyAddress}</Text> ("The Property").
          </Text>

          <Text style={styles.paragraph}>
            Global Guardians provides a sustainable community security solution that makes you stand out. Our Guardianship strategy is designed to make a positive impact on not only your daily life but the town, city or village you live within.
          </Text>

          <Text style={styles.paragraph}>
            We place professional working people form your local community into your empty property, as part of your security solution.
          </Text>

          <Text style={styles.paragraph}>
            Like you, we are concerned by the growing number of empty properties and the risk of squatters. Nobody should have to experience sleepless nights when managing a property.
          </Text>

          <Text style={styles.paragraph}>Let us take care of your space.</Text>

          <Text style={styles.sectionTitle}>Key Information</Text>

          <View style={styles.keyInfoTable}>
            <View style={styles.keyInfoHeader}>
              <Text style={styles.keyInfoHeaderCell}></Text>
              <Text style={styles.keyInfoHeaderCell}></Text>
            </View>
            <View style={styles.keyInfoRow}>
              <Text style={styles.keyInfoLabel}>Minimum Contract Period</Text>
              <Text style={styles.keyInfoValue}>{data.npp_minimum_contract_period || data.min_contract_period_word || ''}</Text>
            </View>
            <View style={styles.keyInfoRow}>
              <Text style={styles.keyInfoLabel}>Start Date</Text>
              <Text style={styles.keyInfoValue}>Date the contract is signed by The Owner</Text>
            </View>
            <View style={styles.keyInfoRow}>
              <Text style={styles.keyInfoLabel}>Notice Period</Text>
              <Text style={styles.keyInfoValue}>{data.npp_notice_period || ''}</Text>
            </View>
            <View style={styles.keyInfoRow}>
              <Text style={styles.keyInfoLabel}>GGM's Emergency Number</Text>
              <Text style={styles.keyInfoValue}>0800 368 9601</Text>
            </View>
            <View style={styles.keyInfoRow}>
              <Text style={styles.keyInfoLabel}>Key Collection Date</Text>
              <Text style={styles.keyInfoValue}>Within 48 hours of receipt of signed proposal</Text>
            </View>
            <View style={styles.keyInfoRowLast}>
              <Text style={styles.keyInfoLabel}>Revenue Share</Text>
              <Text style={styles.keyInfoValue}>{data.npp_revenue_share || ''}</Text>
            </View>
          </View>

          <Text style={styles.noteText}>
            GGM will only accept liability once the keys are in our possession.
          </Text>
        </View>
      </Page>

      {/* Page 5: Empty page */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={{ color: 'white' }}> </Text>
        </View>
      </Page>

      {/* Page 6: Table of Contents */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Table of Contents</Text>
          {tocItems.map((item, index) => (
            <View key={index} style={styles.tocItem}>
              <Text style={styles.tocText}>{item.title}</Text>
              <Text style={styles.tocPage}>{item.page}</Text>
            </View>
          ))}
        </View>
      </Page>

      {/* Page 7: Our Guardian Security Solution */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Our Guardian Security Solution</Text>

          <Text style={styles.paragraph}>
            We are here to guide you on how to take control of your property. Your peace of mind is our main priority. We place professional working people from the local community into your empty property so you don't need to worry about anti-social behaviour, squatting, arson and metal theft.
          </Text>

          <Text style={styles.paragraph}>
            Our unique security solution continues to help thousands of partners protect their empty commercial and residential properties. How?
          </Text>

          <Text style={styles.sectionTitle}>My Globe provides you with visibility and control. My Globe shows you:</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Monthly Inspections photos and videos</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Up-to-date certification</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Cost savings per property</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Your property portfolio in one location</Text>
            </View>
          </View>

          <Text style={styles.sectionTitle}>The Empty Property Protection Plan keeps you in safe hands:</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Your property will be fully compliant. This includes fire safety, legionella management, asbestos management, gas safety and NIC certification</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>We carry out reactive and preventative maintenance works</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Your property will be kept in safe hands by our Client Care and Guardian Care team at all times</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Guardians residing in your property will be vetted and onboarded into the unique lifestyle</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Global Guardians won a major legal victory in 2021 which means there is no longer any legal defence for Guardians who choose to break their Licence Agreement and not move out when a NTQ (Notice To Quit) is issued</Text>
            </View>
          </View>

          <Text style={styles.sectionTitle}>Our brand values are at the heart of everything we do:</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Safe Hands - your building is looked after 24/7 and your reputation is protected</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Progressive- we always provide you with the most important about your property</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Inclusive - we include you in all the decisions made about your property</Text>
            </View>
          </View>

          <Text style={styles.highlightedText}>We want to take care of your building</Text>
        </View>
      </Page>

      {/* Page 8: Authorisation for the use of the Property */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Authorisation for the use of the Property</Text>

          <Text style={styles.paragraph}>
            For the duration of the Proposal, the Owner grants possession of the Property to GGM for the purposes of securing it against trespassers and protecting it from damage. The Owner agrees that occupation by multiple guardians will not create a licensable House in Multiple Occupation owing to the exemption from licensing afforded to properties controlled or managed by public bodies under Sch 14 Housing Act 2014
          </Text>

          <Text style={styles.paragraph}>
            GGM may permit G100 to grant Licences to Guardians for the purpose of securing the Property against trespassers and protecting it from damage. GGM or G100 may change the existing locks, and may add additional locks.
          </Text>

          <Text style={styles.paragraph}>At no time shall GGM or G100;</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Occupy the Property themselves or by their servants or agents.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Grant or purport to grant any Tenancy of the Property or any part of it.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Allow any person to occupy the Property or any part of it; other than Guardians under Licences.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Carry on a business in all of any part of the Property.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Control or Manage the property in terms of Section 263 Housing Act 2004</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Be responsible for licensing the property under Housing Act 2004 should a license be required notwithstanding the exemption under Schedule 14 referred to above and agreed as effective by the parties</Text>
            </View>
          </View>

          <Text style={styles.sectionTitle}>Duration</Text>

          <Text style={styles.paragraph}>
            The Proposal begins on the Start Date and ends on Termination. The Proposal may be terminated by the Owner at any time by giving GGM not less than Thirty Two Days written notice to terminate the Agreement. Such a notice must expire on a Wednesday.
          </Text>

          <Text style={styles.paragraph}>
            The Proposal may be terminated by GGM at any time by giving the Owner not less than thirty-two days' written notice to terminate the Agreement. Such a notice may expire at any time.
          </Text>

          <Text style={styles.sectionTitle}>Bespoke Property Management Software</Text>

          <Text style={styles.paragraph}>
            My Globe is a completely bespoke system built by GGM using many years of experience in the Property Guardian and PropTech Industry.
          </Text>

          <Text style={styles.paragraph}>
            My Globe is utilised by everyone associated with GGM, as it is a centralised system specialising on focused work. All documentation and reporting is used on the system so it is easy to retrieve information uploaded, either five minutes or five years previously. This allows GGM to scale up and take on any type of project no matter how large.
          </Text>

          <Text style={styles.paragraph}>
            The Owner is able to access their My Globe portal under a "Client Account" which stores all Property details under GGM's management and can be accessed with unique login information. Our secured server will hold all details and documents relating to the Property and this live data will be uploaded directly by members of our dedicated Property Management Team.
          </Text>

          <Text style={styles.paragraph}>
            To access the portal, please use the following link; www.my-globe.co.uk
          </Text>
        </View>
      </Page>

      {/* Page 9: More sections */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.sectionTitle}>General Data Protection Regulation (GDPR)</Text>

          <Text style={styles.paragraph}>
            GGM operates under the General Data Protection Regulation (GDPR) as we are fully committed to protecting the rights and privacy of individuals, in accordance with the Data Protection Act 1998 and the GDPR Legislation which was enforced on 25th May 2018.
          </Text>

          <Text style={styles.paragraph}>
            Information about GGM's personnel, the Owner and other individuals will only be processed in line with established regulations and lawful purpose. Personal data will be collected, recorded and used fairly, stored safely and securely, in a transparent manner and not disclosed to any third party unlawfully.
          </Text>

          <Text style={styles.paragraph}>
            The lawful and correct treatment of personal information is critical to GGM's successful operations and to maintaining confidence. GGM places high importance on the fair handling of all personal data, respecting the legal rights, privacy, and trust of all individuals with whom it deals.
          </Text>

          <Text style={styles.sectionTitle}>Liability Insurance</Text>

          <Text style={styles.paragraph}>
            GGM has comprehensive insurance which can be provided on request. GGM has £5,000,000 Public Liability, £5,000,000 Employer's Liability and £5,000,000 Professional Indemnity. GGM requests to obtain written details of the Owner's insurance policy requirements for vacant Properties, to help GGM inform the selection of services that might be required.
          </Text>

          <Text style={styles.sectionTitle}>Building Insurance</Text>

          <Text style={styles.paragraph}>
            GGM requires a copy of the Property insurance upon signature of the Proposal.
          </Text>

          <Text style={styles.sectionTitle}>Risk Management</Text>

          <Text style={styles.paragraph}>
            GGM has the right to charge back any maintenance works to the Owner if the Owner does not complete the works within forty-eight hours.
          </Text>

          <Text style={styles.paragraph}>
            All charge backs will be processed through GGFM and the prices will be plus VAT.
          </Text>

          <Text style={styles.sectionTitle}>Changes to Property Agreement</Text>

          <Text style={styles.paragraph}>
            If the Owner makes changes to the Property, which is not agreed in this Proposal from the start date, then GGM reserves the right to re-negotiate contract terms. If the Owner makes changes that render the Property uninhabitable to guardians, whilst GGM is protecting the property, GGM will make good on the changes at cost to the Owner. If negotiation cannot be reached between the Owner and GGM, then the contract will be terminated forthwith and the Owner will be liable for losses incurred by GGM as stated under the Break Clause.
          </Text>

          <Text style={styles.sectionTitle}>Force Majeure</Text>

          <Text style={styles.paragraph}>
            A Party to this agreement (Affected Party) shall not be liable to the other Party or be in breach for any delay or failure to perform any of its obligations under this Proposal that is caused by a Force Majeure Event for the period in which a Force Majeure Event subsists provided it complies with the requirements of this clause.
          </Text>
        </View>
      </Page>

      {/* Page 10: Force Majeure continued */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            On the occurrence of a Force Majeure Event, the Affected Party shall give immediate written notice to the other Party of;
          </Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>The nature of the Force Majeure Event.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>How the Force Majeure Event is affecting the performance of its obligations under this Property Protection Proposal.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>The date upon which the Force Majeure Event began to affect its performance of its obligations under this Proposal.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>The estimated period during which its performance of its obligations under this Proposal will be affected.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>The action it has taken and proposes to take to mitigate the effects of a Force Majeure Event.</Text>
            </View>
          </View>

          <Text style={styles.paragraph}>
            The Affected Party shall provide regular updates on any Force Majeure Event and shall notify the other Party. The Affected Party shall mitigate (and whilst it continues, shall continue to mitigate) the effects of the Force Majeure Event on its performance in accordance with Good Industry Practice (where the Tenant is the Affected Party). GGM shall continue to perform obligations which are not affected by the Force Majeure Event and shall not in the allocation of available resources treat any other customer more favourably than The Owner.
          </Text>

          <Text style={styles.paragraph}>
            The Parties shall consult with each other and take reasonable steps to agree appropriate action to further mitigate the effects of the Force Majeure Event and facilitate continued performance of this Lease. Where GGM is the Affected Party it shall ensure that it does not, in the allocation of available resources, treat any other customer more favourably than The Owner.
          </Text>

          <Text style={styles.paragraph}>
            Immediately upon a Force Majeure Event ceasing to subsist clause the Affected Party shall be liable for the performance of its obligations in accordance with the terms of this Proposal.
          </Text>

          <Text style={styles.paragraph}>
            The non-Affected Party may at any time following written notice in accordance with this section but before a Force Majeure Event ceases to subsist, terminate this Proposal by notice in writing to the Affected Party (such termination to be without prejudice to any outstanding liability of either Party to the other).
          </Text>

          <Text style={styles.sectionTitle}>Marketing the Property</Text>

          <Text style={styles.paragraph}>
            GGM will place appropriate signs in the windows and to the exterior of the Property to notify passers-by that the Property is legally occupied and protected by Property Guardians. This visible signature also acts as a deterrent to would be squatters and vandals. GGM retains the right to use the Owner's name and corporate logo in all of its marketing material.
          </Text>

          <Text style={styles.sectionTitle}>Neighbours</Text>

          <Text style={styles.paragraph}>
            If the Owner specifically requests GGM to do so, GGM will write a letter to all neighbouring properties as well as the local police station, safer neighbourhoods' team, local council and fire brigade advising that the Property is being legally resided in and protected by GGM. Should the Owner wish for us to send these letters then the Owner will need to provide GGM with a list of applicable addresses to send them to.
          </Text>

          <Text style={styles.highlightedText}>
            GGM will liaise with neighbours throughout our management via our Project Manager, Compliance Officers and Head Guardians.
          </Text>
        </View>
      </Page>

      {/* Page 11: General Liability Schedule */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>General Liability Schedule</Text>

          <View style={styles.liabilityTable}>
            <View style={styles.liabilityHeader}>
              <Text style={[styles.liabilityHeaderCell, { width: '30%' }]}></Text>
              <Text style={[styles.liabilityHeaderCell, { width: '23%' }]}>Responsibility of completing works</Text>
              <Text style={[styles.liabilityHeaderCell, { width: '23%' }]}>Responsibility of payment</Text>
              <Text style={[styles.liabilityHeaderCellLast, { width: '24%' }]}>Estimated Cost of works</Text>
            </View>
            {getGeneralLiabilityItems(data).map((item, index, arr) => (
              <View key={index} style={index === arr.length - 1 ? styles.liabilityRowLast : styles.liabilityRow}>
                <Text style={[styles.liabilityLabelCell, { width: '30%' }]}>{item.item}</Text>
                <Text style={[styles.liabilityCell, { width: '23%' }]}>{item.works}</Text>
                <Text style={[styles.liabilityCell, { width: '23%' }]}>{item.payment}</Text>
                <Text style={[styles.liabilityCellLast, { width: '24%' }]}>{item.cost}</Text>
              </View>
            ))}
          </View>

          <View>
            <Text style={styles.pageTitle}>Utility Liability Schedule</Text>

            <View style={styles.liabilityTable}>
              <View style={styles.liabilityHeader}>
                <Text style={[styles.liabilityHeaderCell, { width: '25%' }]}></Text>
                <Text style={[styles.liabilityHeaderCell, { width: '25%' }]}>Responsibility of completing works</Text>
                <Text style={[styles.liabilityHeaderCell, { width: '25%' }]}>Responsibility of payment</Text>
                <Text style={[styles.liabilityHeaderCellLast, { width: '25%' }]}>Current supplier and account number</Text>
              </View>
              {getUtilityLiabilityItems(data).map((item, index, arr) => (
                <View key={index} style={index === arr.length - 1 ? styles.liabilityRowLast : styles.liabilityRow}>
                  <Text style={[styles.liabilityLabelCell, { width: '25%' }]}>{item.item}</Text>
                  <Text style={[styles.liabilityCell, { width: '25%' }]}>{item.works}</Text>
                  <Text style={[styles.liabilityCell, { width: '25%' }]}>{item.payment}</Text>
                  <Text style={[styles.liabilityCellLast, { width: '25%' }]}>{item.supplier}</Text>
                </View>
              ))}
            </View>

            <Text style={styles.noteText}>
              GGM will not be liable for any outstanding debts prior to occupation of the Property.
            </Text>
          </View>
        </View>
      </Page>

      {/* Page 12: Health & Safety Liability Schedule */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Health and Safety Liability Schedule</Text>

          <View style={styles.liabilityTable}>
            <View style={styles.liabilityHeader}>
              <Text style={[styles.liabilityHeaderCell, { width: '25%' }]}></Text>
              <Text style={[styles.liabilityHeaderCell, { width: '18%' }]}>Responsibility of completing works</Text>
              <Text style={[styles.liabilityHeaderCell, { width: '18%' }]}>Responsibility of payment</Text>
              <Text style={[styles.liabilityHeaderCell, { width: '15%' }]}>Documentation Required</Text>
              <Text style={[styles.liabilityHeaderCellLast, { width: '24%' }]}>Estimated Cost of Works</Text>
            </View>
            {getHealthSafetyItems(data).map((item, index, arr) => (
              <View key={index} style={index === arr.length - 1 ? styles.liabilityRowLast : styles.liabilityRow}>
                <Text style={[styles.liabilityLabelCell, { width: '25%' }]}>{item.item}</Text>
                <Text style={[styles.liabilityCell, { width: '18%' }]}>{item.works}</Text>
                <Text style={[styles.liabilityCell, { width: '18%' }]}>{item.payment}</Text>
                <Text style={[styles.liabilityCell, { width: '15%' }]}>{item.required}</Text>
                <Text style={[styles.liabilityCellLast, { width: '24%' }]}>{item.cost}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.noteText}>
            GGM requires the Owner to attach all the documentation and certification listed above for the Property.
          </Text>
        </View>
      </Page>

      {/* Page 13: Maintenance Liability Schedule */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Maintenance Liability Schedule</Text>

          <View style={styles.liabilityTable}>
            <View style={styles.liabilityHeader}>
              <Text style={[styles.liabilityHeaderCell, { width: '34%' }]}></Text>
              <Text style={[styles.liabilityHeaderCell, { width: '22%' }]}>Responsibility of completing works</Text>
              <Text style={[styles.liabilityHeaderCell, { width: '22%' }]}>Responsibility of payment</Text>
              <Text style={[styles.liabilityHeaderCellLast, { width: '22%' }]}>Estimated Cost of works</Text>
            </View>
            {getMaintenanceItems(data).map((item, index, arr) => (
              <View key={index} style={index === arr.length - 1 ? styles.liabilityRowLast : styles.liabilityRow}>
                <Text style={[styles.liabilityLabelCell, { width: '34%' }]}>{item.item}</Text>
                <Text style={[styles.liabilityCell, { width: '22%' }]}>{item.works}</Text>
                <Text style={[styles.liabilityCell, { width: '22%' }]}>{item.payment}</Text>
                <Text style={[styles.liabilityCellLast, { width: '22%' }]}>{item.cost}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.paragraph}>
            Where required, GGM will install Temporary Facilities at The Property in a good and workmanlike manner. GGM will remove the Temporary Facilities from The Property on Termination, and will make good any damage caused by their removal.
          </Text>

          <Text style={styles.noteText}>
            All estimated cost of works are subject to VAT.
          </Text>
        </View>
      </Page>

      {/* Page 14: Definitions */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Definitions</Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Proposal"</Text> means this Property Protection Proposal.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Guardian(s)"</Text> means the person/s sharing the occupation of the Property, or part of it, under licences.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Licence(s)"</Text> means the temporary non-exclusive licence(s) to share occupation of the Property or part of it which do not confer any right to the exclusive possession of the Property or any part of it and which will come to an end on or before Termination.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"G100"</Text> means Global 100 Limited, Company Number 768230, whose registered office is situated at Evolution House, Iceni Court, Delft Way, Norwich, Norfolk, NR6 6BB.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"GGM"</Text> means Global Guardians Management Limited, Company Number 7676554, whose registered office is situated at Evolution House, Iceni Court, Delft Way, Norwich, Norfolk, NR6 6BB.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"GGFM"</Text> means Global Guardians Facilities Management Limited, Company Number 11383856, whose registered office is situated at Evolution House, Iceni Court, Delft Way, Norwich, Norfolk, NR6 6BB.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Minimum Contract Period"</Text> means the minimum contract period specified under Key Information.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Owner"</Text> means the Property, Building and Grounds, identified in this Proposal.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Property Protection Proposal"</Text> means this Proposal from GGM offering Property protection services in respect of the Property identified in the Proposal, to be signed by the Owner.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Start Date"</Text> means the date on which GGM received this Proposal signed by the Owner.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Start-up Fees"</Text> means the fees payable by the Owner at the Commencement Date of the Proposal as set out in the Liability Schedule attached to the Proposal.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Temporary Facilities"</Text> means the temporary facilities specified in this Proposal or, if no facilities are so specified, such temporary as GGM shall consider necessary to enable the Property to be occupied by Guardians.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>Termination"</Text> means the coming to an end of this Proposal.
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Weekly Fee"</Text> means the weekly management fee specified in the General Liability Schedule in this Proposal (including any fee for refuse disposal).
          </Text>

          <Text style={styles.paragraph}>
            <Text style={styles.boldText}>"The Party"</Text> means any persons affected by this Property Protection Proposal such as the Owner, GGM, G100 and/or GGFM.
          </Text>
        </View>
      </Page>

      {/* Page 15: Interpretations */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Interpretations</Text>

          <Text style={styles.paragraph}>
            The clause headings do not affect the meaning of this Proposal. Words of one gender include all other genders and any reference to a person includes a reference to a Company, Authority, Board, Department or other Body. Any obligation not to do something includes an obligation not to permit or encourage anyone else to do it, and to make all reasonable efforts to ensure that other persons do not do it. Any consent, approval, authorisation or notice under the Proposal will only take effect if given in writing. If the Owner is more than one person, the obligations of each such person are joint and several.
          </Text>
        </View>
      </Page>

      {/* Page 16: Accepting this Offer */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Accepting this Offer</Text>

          <Text style={styles.paragraph}>
            GGM trusts this Property Protection Proposal reflects the discussions accurately and meets with the Owner's approval. Please sign and date this proposal to confirm your acceptance.
          </Text>

          <Text style={styles.boldText}>
            Global Guardians Management Limited looks forward to working with you to protect and preserve your vacant Property.
          </Text>

          <Text style={{ marginTop: 20, marginBottom: 10 }}>Yours Sincerely,</Text>

          <View style={styles.signatureBox}>
            <Image src="/assets/images/signature_sw.png" style={styles.signatureImage} />
          </View>
          <Text style={styles.signatureName}>Stuart Woolgar</Text>
          <Text style={styles.signatureTitle}>Chief Executive Officer</Text>
          <Text style={styles.signatureTitle}>Global Guardians Management Limited</Text>

          {/* <Text style={styles.signatureName}>Theo Kyprianou</Text>
          <Text style={styles.signatureTitle}>Managing Director</Text>
          <Text style={styles.signatureTitle}>Global Guardians Management Limited</Text> */}

          <View style={{ marginTop: 40 }}>
            <Text style={styles.paragraph}>
              I am the Owner/the duly authorised representative of the Property. I hereby accept this Property Protection Proposal provided by Global Guardians Management Limited for the services as detailed herein and on the Terms and Conditions provided.
            </Text>

            <Text style={{ marginTop: 15, marginBottom: 5 }}>Name: {contactName}</Text>
            <View style={styles.signatureLine} />
            <Text style={{ marginBottom: 5 }}>Signature: ..................................................................................</Text>
            <Text style={{ marginBottom: 5 }}>Date: ................................................................................................</Text>
            <Text style={{ marginBottom: 5 }}>Financial contact: ...................................................................</Text>
            <Text style={{ marginBottom: 5 }}>Owner/Agent: ............................................................................</Text>
          </View>
        </View>
      </Page>

      {/* Page 17: Terms and Conditions */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.pageTitle}>Terms and Conditions</Text>

          <Text style={styles.subsectionTitle}>Owner's Obligation</Text>
          <Text style={styles.paragraph}>During the Proposal, The Owner will:</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that the Property is empty on the Start Date.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>On or before the Start Date, provide GGM with a set of keys to all existing locks at the Property.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Maintain the structure and exterior of the Property in a wind and watertight condition.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Maintain insurance in respect of the Property against loss or damage by fire and other usual commercial risks together with Third Party and Occupier's Liability Risks.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Maintain Asbestos and Legionella Certification, Gas Safety Certification and NIC Certification.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Maintain any fire alarm system and fire extinguishers at the Property, any emergency escape routes at or serving the Property and any emergency, fire or electrical signs or notices at the Property.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>If it exercises its right to inspect the Property, not to cause any undue disruption or inconvenience to the Guardian or any damage to any Property of the Guardians, G100 or GGM.</Text>
            </View>
          </View>

          <Text style={styles.subsectionTitle}>GGM's Obligation</Text>
          <Text style={styles.paragraph}>During the Proposal, GGM will:</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that G100 arranges for suitable Guardians to occupy the Property under Licences.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Arrange for regular inspections of the Property to be carried out by experienced Property Inspectors.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Report to the Owner any damage and/or trespass to or at the Property as soon as reasonably possible.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that G100 and GGM both use reasonable care to ensure that no damage is caused to the Property.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that G100 enforces the obligations on the part of the Guardian contained in the Licences.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that there is in place insurance at all times in a suitable amount covering G100 and GGM against liability for injury or loss to third parties.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that the Owner may inspect the Property at all reasonable times, providing that the Owner gives no less than twenty-four hours written notice of such inspection to GGM.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Commence and diligently pursue proceedings to evict any Guardian who fails to vacate the Property when lawfully obliged to do so.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Maintain confidentiality with respect to all information obtained whilst carrying out its obligations.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Immediately surrender any keys to an authorised representative of the Owner if requested by the Owner in writing upon termination of the Proposal.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Keep all areas within the Property that are designed or adapted for common use in such a condition as to be capable of reasonable use. These include, for example (but are not limited to) hallways, stairways, corridors, kitchen, toilet and bathroom facilities.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Ensure that any works are carried out which are required under relevant legislation for Guardian living, affecting the Property, including any required under health and safety legislation.</Text>
            </View>
          </View>
        </View>
      </Page>

      {/* Page 18: Vacating & Declarations */}
      <Page size="A4" style={styles.contentPage}>
        <PageHeader />
        <View style={styles.content}>
          <Text style={styles.sectionTitle}>Vacating the Property after Termination</Text>

          <Text style={styles.paragraph}>
            Upon Termination, GGM will yield up possession of the Property to the Owner of the Property and all goods in the Property belonging to G100 and GGM are removed and all keys are returned to the Owner.
          </Text>

          <Text style={styles.paragraph}>
            If at Termination, any Guardian is still in occupation of the Property then GGM will co-operate with the Owner in securing the removal of any remaining Guardians from the Property and will ensure that G100 also provides such co-operation. Such co-operation may include commencing a claim for possession against the Guardian
          </Text>

          <Text style={styles.paragraph}>
            This agreement creates a license to GGM who in turn may grant a license to G100. These licenses create immediate rights to possession, for the purpose of obtaining possession against any Guardian who has failed or refused to move after being given notice to do so and that notice has expired. Specifically, G100 will have an immediate right to possession as against any licensee it licensed to occupy the property and GGM will have an immediate right to possession as against any trespasser or squatter who might be found in the property after the time of signing this licence and prior to GGM having entered into any licence arrangements with G100.
          </Text>

          <Text style={styles.paragraph}>
            In so seeking to recover possession, G100 or where appropriate, GGM, will be responsible for legal costs involved in removing a Guardian from the Property. The sums payable by G100 or as the case may be, GGM, shall be limited in total to the maximum figure of £10,000.00 in relation to all or any losses, damages, liabilities, costs or any other sums of any description incurred by or liable to be incurred by the Owner directly or indirectly or by any means howsoever by reason of or flowing from any failure by G100 or GGM to hand back vacant possession at the date of lawful termination of the Proposal. For the avoidance of doubt, this is the total sum recoverable by the Owner, not the total recoverable by the Owner from each of G100 and GGM and this is limited to the individual Property as per this Proposal.
          </Text>

          <Text style={styles.paragraph}>
            The Parties have entered this Proposal on the basis of the Owner's rights to recover money being limited to the sum set out above.
          </Text>

          <Text style={styles.sectionTitle}>Declarations, Warranties and Miscellaneous</Text>

          <Text style={styles.paragraph}>The Parties agree that:</Text>

          <View style={styles.bulletList}>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Any dispute will be determined subject to English Law by Courts subject to English Law jurisdiction.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>No officer, employee or agent of GGM has or had any authority to give any oral or written information about or in relation to the Proposal or services provided by GGM other than the information in this Property Protection Proposal and any written information supplied directly to the Owner by GGM.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>The Proposal embodies the entire understanding of the Parties relating to the Property and the matters dealt with by any of the provisions of the Proposal. GGM undertakes no obligations to the Owner except those expressly set out in the Proposal.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Nothing in the Proposal confers any right or imposes any obligation on any person who is not a party to it, whether under the Contract (Rights of Third Parties) Act 1999 or otherwise. In particular, the Proposal gives no rights, and imposes no obligations, on any Guardian or G100.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>Guardians have no security, responsibilities or authority as defined by the Private Security Industry Act 2001 of the United Kingdom.</Text>
            </View>
            <View style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>G100, GGM, their employees and agents do not have, and will not assume the powers of the police or other civil protection service.</Text>
            </View>
          </View>

          <Text style={styles.sectionTitle}>Notices</Text>

          <Text style={styles.paragraph}>
            Any notice given under the Proposal shall be in writing signed by (or by some person duly authorised by) the person giving it. Any such notice may be served by leaving it at the address for service, or sending it by pre-paid recorded delivery post, to the address for service as stated below, or sending it by email to ClientCare@global-guardians.co.uk.
          </Text>

          <Text style={styles.paragraph}>
            GGM's address for service is Sopers House, Sopers Road, Cuffley, Potters Bar EN6 4RY to the attention of the Client Care Team.
          </Text>

          <Text style={styles.paragraph}>
            The Owner's address for service is the address to which the Property Protection Proposal was addressed.
          </Text>

          <Text style={styles.paragraph}>
            Either Party may substitute a new address for service by notifying the other Party in writing. Any notice served in the way set out above shall be deemed to have been received, if left at the address for service, at the time of leaving, in the case of a notice sent by pre-paid recorded delivery, forty-eight hours after the date of posting or in the case of a notice sent by email, if the notice is sent during business hours of the addressee then on the day of transmission, and otherwise on the next following business day. "Business hours" mean 09:30am to 17:30pm local time in the country of the recipient.
          </Text>
        </View>
      </Page>

      {/* Page 20: Final Page */}
      <Page size="A4" style={styles.finalPage}>
        <View style={styles.finalContent}>
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.finalLogo}
          />
          <View style={styles.finalAddress}>
            <Text style={styles.finalAddressText}>Global Guardians</Text>
            <Text style={styles.finalAddressText}>Sopers House</Text>
            <Text style={styles.finalAddressText}>Sopers Road</Text>
            <Text style={styles.finalAddressText}>Cuffley</Text>
            <Text style={styles.finalAddressText}>Potters Bar</Text>
            <Text style={styles.finalAddressText}>EN6 4RY</Text>
            <Text style={styles.finalContactText}>0800 368 9601</Text>
            <Text style={styles.finalAddressText}>clientcare@global-guardians.co.uk</Text>
            <Text style={styles.finalContactText}>www.global-guardians.co.uk</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default StandardProposalPDF;
